import { useQueryClient } from '@tanstack/react-query';
import { Form, Formik, FormikHelpers } from 'formik';
import { KeyboardEvent, useEffect, useMemo, useState } from 'react';
import { FaPlus } from 'react-icons/fa6';
import { TbSquareRoundedPlusFilled } from 'react-icons/tb';
import { toast } from 'react-toastify';
import {
  SELECT_FIELD_DEFAULT_VALUE,
  VALID_STRING_REGEX,
} from '../../../constants/settings';
import { useAuthContext } from '../../../contexts/AuthProvider';
import useModelActions from '../../../hooks/APIHooks/useModelActions';
import useFeatureActions from '../../../hooks/APIHooks/users/useFeatureActions';
import { FeatureReqBody, ModelSelectData } from '../../../types/features';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import AddFeatureModalTable from '../../SetupSubPageComps/FeaturesComps/AddFeatureModalTable';
import AcademiaAddButton from '../Buttons/AcademiaAddButton';
import AcademiaButton from '../Buttons/AcademiaButton';
import AcademiaFormSelect from '../Inputs/AcademiaFormSelect';
import AcademiaTextAreaField from '../Inputs/AcademiaTextAreaField';
import ModalTitle from './ModalTitle';
import ParentModal from './ParentModal';

const AddFeatureModal = ({
  handleModalClose,
}: {
  handleModalClose: () => void;
}) => {
  // integration of react hooks here
  const [modelList, setModelList] = useState<Array<ModelSelectData>>([]);

  const [selectedFeatures, setSelectedFeatures] = useState<
    Array<FeatureReqBody>
  >([]);
  const [resetFeatureField, setResetFeatureField] = useState<boolean>(false);

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // integration of custom API hooks here
  const { getFeatureListHandler, addNewFeaturesHandler } =
    useFeatureActions('getFeatureList');

  const {
    getUserModelListHandler,
    getAppointmentModelListHandler,
    getFinancialModelListHandler,
  } = useModelActions('allModelList');

  // integration of context hooks here
  const { currentUser } = useAuthContext();

  console.log(
    'CORE FEATURE LIST: ',
    getUserModelListHandler.data?.data,
    getAppointmentModelListHandler.data?.data,
    getFinancialModelListHandler.data?.data
  );

  // merging all the model lists here
  useEffect(() => {
    if (
      getAppointmentModelListHandler.data?.is_success &&
      getFinancialModelListHandler.data?.is_success &&
      getUserModelListHandler.data?.is_success
    ) {
      const appointmentModelList =
        getAppointmentModelListHandler.data.data.map<ModelSelectData>(
          (model) => ({ ...model, service_name: 'Appointment' })
        );

      const financialModelList =
        getFinancialModelListHandler.data.data.map<ModelSelectData>(
          (model) => ({
            ...model,
            service_name: 'Finance',
          })
        );

      const userModelList = getUserModelListHandler.data.data
        .filter((model) => !model.label.includes('Token'))
        .map<ModelSelectData>((model) => ({ ...model, service_name: 'Users' }));

      setModelList(
        appointmentModelList.concat(financialModelList).concat(userModelList)
      );
    }
  }, [
    getAppointmentModelListHandler.data,
    getFinancialModelListHandler.data,
    getUserModelListHandler.data,
  ]);

  // formatting feature list here
  const featureList = useMemo(() => {
    return (
      modelList
        .filter(
          (model) =>
            !getFeatureListHandler.data?.data.some(
              (feature) => feature.name === model.model_name
            )
        )
        .filter(
          (model) =>
            !selectedFeatures.some((feature) => feature.name === model.label)
        )
        .map((model, index) => ({
          id: index,
          name: model.label,
        })) || []
    );
  }, [getFeatureListHandler.data?.data, modelList, selectedFeatures]);

  // formatting feature description list here
  const featureDescriptionList = useMemo(() => {
    return modelList
      .filter(
        (model) =>
          !getFeatureListHandler.data?.data.some(
            (feature) => feature.name === model.model_name
          )
      )
      .filter(
        (model) =>
          !selectedFeatures.some((feature) => feature.name === model.label)
      )
      .map((model) => `(${model.service_name})`);
  }, [getFeatureListHandler.data?.data, modelList, selectedFeatures]);

  // handler function to handle submitting selected features declared here
  const submitSelectedFeaturesHandler = async (
    values: any,
    actions: FormikHelpers<any>
  ) => {
    try {
      actions.setSubmitting(true);

      await addNewFeaturesHandler
        .mutateAsync({
          body: selectedFeatures.map((feature) => ({
            ...feature,
            name: feature.name.split(' ').join(''),
          })),
        })
        .then((res) => {
          if (res.is_success) {
            toast.success(
              selectedFeatures.length > 1
                ? 'Features Added!!'
                : 'Feature Added!!'
            );

            handleModalClose();

            queryClient.invalidateQueries({ queryKey: ['feature-list'] });
          } else {
            toast.error(res.message || 'Feature Adding Failed!!');
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      actions.setSubmitting(false);
    }
  };

  // handler function to add selected feature to the list declared here
  const addSelectedFeatureHandler = (
    featureName: string,
    description: string
  ) => {
    setSelectedFeatures((state) => [
      ...state,
      {
        name: featureName,
        description,
        created_by: currentUser?.username as string,
      },
    ]);
  };

  // handler function for removing selected features from the list declared here
  const removeSelectedFeatureHandler = (featureName: string) => {
    setSelectedFeatures((state) =>
      state.filter((feature) => feature.name !== featureName)
    );
  };

  // rendering create feature modal component here
  return (
    <ParentModal onClick={handleModalClose} customWidthClass='md:w-[700px]'>
      <ModalTitle titleText='Add feature' />

      <Formik
        initialValues={{
          selected_feature: SELECT_FIELD_DEFAULT_VALUE,
          description: '',
        }}
        onSubmit={submitSelectedFeaturesHandler}
      >
        {(props) => (
          <Form className='space-y-5'>
            <AcademiaFormSelect
              name='selected_feature'
              label='Select Feature'
              options={featureList}
              optionDescription={featureDescriptionList}
              defaultValue={SELECT_FIELD_DEFAULT_VALUE}
              resetSelectField={resetFeatureField}
              setResetSelectField={() => setResetFeatureField(false)}
              showSearchInput
              isRequired
              isDisabled={!modelList.length}
            />

            <div className='space-y-3'>
              <AcademiaTextAreaField
                name='description'
                label='Description'
                placeholder='Enter description here...'
                onKeyDown={(e: KeyboardEvent<HTMLTextAreaElement>) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (
                      props.values.selected_feature.id !== -1 &&
                      props.values.description
                    ) {
                      addSelectedFeatureHandler(
                        props.values.selected_feature.name,
                        props.values.description
                      );

                      setResetFeatureField(true);
                      props.setFieldValue(
                        'selected_feature',
                        SELECT_FIELD_DEFAULT_VALUE
                      );
                      props.setFieldValue('description', '');
                    } else {
                      toast.warn('Please, Select Feature Properly!!');
                    }
                  }
                }}
                isRequired
              />

              <div className='flex items-center justify-end'>
                <AcademiaAddButton
                  title={
                    props.values.selected_feature.id === -1
                      ? 'Please, select a feature first!'
                      : !VALID_STRING_REGEX.test(props.values.description)
                      ? 'Please, add a proper description first!'
                      : ''
                  }
                  onClick={() => {
                    addSelectedFeatureHandler(
                      props.values.selected_feature.name,
                      props.values.description
                    );

                    setResetFeatureField(true);
                    props.setFieldValue(
                      'selected_feature',
                      SELECT_FIELD_DEFAULT_VALUE
                    );
                    props.setFieldValue('description', '');
                  }}
                  isDisabled={
                    props.values.selected_feature.id === -1 ||
                    !VALID_STRING_REGEX.test(props.values.description)
                  }
                >
                  <p className='flex items-center justify-center gap-1'>
                    <FaPlus />
                    <span>Add feature</span>
                  </p>
                </AcademiaAddButton>
              </div>
            </div>

            {selectedFeatures.length ? (
              <div className='overflow-auto'>
                <AddFeatureModalTable
                  selectedFeatures={selectedFeatures}
                  removeSelectedFeatureHandler={removeSelectedFeatureHandler}
                />
              </div>
            ) : null}

            <AcademiaButton
              title={
                selectedFeatures.length === 0
                  ? 'Please, add at least one feature first!'
                  : ''
              }
              type='submit'
              variant='premium'
              icon={<TbSquareRoundedPlusFilled className='size-6' />}
              isLoading={props.isSubmitting}
              isDisabled={selectedFeatures.length === 0}
              fullWidth
            >
              Submit Features
            </AcademiaButton>
          </Form>
        )}
      </Formik>
    </ParentModal>
  );
};

export default AddFeatureModal;
